<template>
    <div class="data-table">
        <div class="main-table">
            <table class="w-full mb-4 d-table">
                <thead>
                    <tr>
                        <th
                            v-for="column in columns" :key="column" class="bg-gray-500 px-2 py-4 text-black text-left"
                            @click="sortByColumn(column)"
                        >
                            <div class="flex items-center">
                                {{ column | columnHead }}
                                <span v-if="column === sortedColumn" class="ml-1">
                                    <Icon v-if="order === 'asc'" name="arrow-up" />
                                    <Icon v-else name="arrow-down" />
                                </span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="tableData.length === 0">
                        <td class="lead text-center" :colspan="columns.length + 1">No data found.</td>
                    </tr>
                    <tr v-for="data in tableData" v-else :key="data.id">
                        <td
                            v-for="(value, key) in rowColumns(data)"
                            :key="key"
                            class="border-b border-gray-600 px-2 py-3"
                        >
                            <div v-if="key === 'actions'" class="flex items-center">
                                <a v-if="value.view" :href="value.view" class="mr-2 hover:text-red-100">
                                    <Icon name="eye" />
                                </a>
                                <a v-if="value.edit" :href="value.edit" class="mr-2">
                                    <Icon name="edit-3" />
                                </a>
                                <button v-if="value.delete" @click="deleteRow(data)">
                                    <Icon name="trash-2" />
                                </button>
                            </div>
                            <template v-else>
                                {{ value }}
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <nav v-if="pagination && tableData.length > 0" class="flex items-center">
            <div class="flex items-center">
                <span
                    v-if="currentPage === 1"
                    class="rounded-l rounded-sm border border-red-100 px-3 py-2
                    cursor-not-allowed no-underline"
                >
                    &laquo;
                </span>
                <button
                    v-else
                    class="rounded-l rounded-sm border-t border-b border-l
                    border-red-100 px-3 py-2 text-brand-dark hover:bg-red-100
                    hover:text-white no-underline"
                    @click.prevent="changePage(currentPage - 1)"
                >
                    &laquo;
                </button>
                <span
                    v-for="(page, index) in pagesNumber"
                    :key="index"
                >
                    <span
                        v-if="page === pagination.meta.current_page"
                        class="block border-t border-b border-l border-red-100
                        px-3 py-2 bg-red-100 no-underline text-white"
                    >
                        {{ page }}
                    </span>
                    <button
                        v-else
                        class="border-t border-b border-l border-red-100 px-3
                        py-2 hover:bg-red-100 hover:text-white text-brand-dark
                        no-underline"
                        @click.prevent="changePage(page)"
                    >
                        {{ page }}
                    </button>
                </span>
                <span
                    v-if="currentPage === pagination.meta.last_page"
                    class="rounded-r rounded-sm border border-red-100 px-3 py-2
                    cursor-not-allowed no-underline"
                >
                    &raquo;
                </span>
                <button
                    v-else
                    class="rounded-r rounded-sm border border-red-100 px-3 py-2
                    hover:bg-red-100 hover:text-white text-brand-dark
                    no-underline"
                    @click.prevent="changePage(currentPage + 1)"
                >
                    &raquo;
                </button>
            </div>
            <span class="ml-auto">
                <i>Displaying {{ pagination.data.length }} of {{ pagination.meta.total }} entries.</i>
            </span>
        </nav>
    </div>
</template>

<script>
    import axios from 'axios';

    export default {
        name: 'DataTable',
        filters: {
            columnHead(value) {
                return value.split('_').join(' ').toUpperCase();
            },
        },
        props: {
            fetchUrl: {
                type: String,
                required: true,
            },
            columns: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                tableData: [],
                url: '',
                pagination: {
                    meta: {
                        to: 1,
                        from: 1,
                    },
                },
                offset: 4,
                currentPage: 1,
                perPage: 16,
                sortedColumn: this.columns[0],
                order: 'asc',
            };
        },
        computed: {
            pagesNumber() {
                if (!this.pagination.meta.to) {
                    return [];
                }

                let from = this.pagination.meta.current_page - this.offset;
                if (from < 1) {
                    from = 1;
                }

                let to = from + (this.offset * 2);
                if (to >= this.pagination.meta.last_page) {
                    to = this.pagination.meta.last_page;
                }

                const pagesArray = [];
                for (let page = from; page <= to; page += 1) {
                    pagesArray.push(page);
                }

                return pagesArray;
            },

            totalData() {
                return (this.pagination.meta.to - this.pagination.meta.from) + 1;
            },

            bodyColumns() {
                const { columns } = this;

                console.log(this.tableData.filter(item => console.log(item)));

                return this.tableData.filter(item => Object.keys(item).filter(key => columns.includes(key)));
            },
        },
        watch: {
            fetchUrl: {
                handler(fetchUrl) {
                    this.url = fetchUrl;
                },
                immediate: true,
            },
        },
        created() {
            this.fetchData();
        },
        methods: {
            async deleteRow(id) {
                if (confirm('Please confirm')) { // eslint-disable-line
                    await axios.delete(this.route('api.users.destroy', id));
                    this.fetchData();
                }
            },

            fetchData() {
                const dataFetchUrl = `${this.url}?page=${this.currentPage}&column=${this.sortedColumn}&order=${this.order}&per_page=${this.perPage}`;
                axios.get(dataFetchUrl)
                    .then(({ data }) => {
                        this.pagination = data;
                        this.tableData = data.data;
                    }).catch(() => { this.tableData = []; });
            },

            changePage(pageNumber) {
                this.currentPage = pageNumber;
                this.fetchData();
            },

            sortByColumn(column) {
                if (column === this.sortedColumn) {
                    this.order = (this.order === 'asc') ? 'desc' : 'asc';
                } else {
                    this.sortedColumn = column;
                    this.order = 'asc';
                }

                this.fetchData();
            },

            rowColumns(data) {
                const obj = { ...data };
                const deleteColumns = this.columns
                    .filter(x => !Object.keys(obj).includes(x))
                    .concat(Object.keys(obj).filter(x => !this.columns.includes(x)));

                deleteColumns.forEach(key => delete obj[key]);

                return obj;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .data-table {
        .main-table {
            .table {
                @apply .w-full mb-1;

                thead th {
                    text-align: left;
                    vertical-align: bottom;
                    border-bottom: 2px solid #dee2e6;
                }

                td, th {
                    vertical-align: top;
                    border-top: 1px solid #dee2e6;
                }
            }
        }
    }
</style>
